import { useState } from 'react'
import { Button, Field, Input, Modal, Notice, Select, Table, type ColumnDef } from '../design/index.js'
import type { LineRow } from '../app/api.js'
import { asText } from './format.js'

/**
 * Billing Details › billing terms, as the reference draws them (docs/05 §1).
 *
 * One row per term — Billing Term · % of Supply · % of Installation — and a
 * Billing Term dialog with the two sections (Supply Value, Installation
 * Value), each its own little grid with a running total. Underneath it is
 * still the BILLING_TERM lines it always was: a line per slice, sharing the
 * term's name, which is what BOQ lines point at and what the
 * billing_term_percentages rule checks. This only makes them editable the
 * way a person thinks about them, and says what is wrong before Done.
 */

type Half = 'SUPPLY' | 'INSTALLATION'

interface Slice {
  readonly key: string
  readonly id?: string
  readonly seq?: number
  readonly name: string
  readonly percentage: string
  readonly linkedTo: string
}

interface Draft {
  /** The name it had when opened; null for a new term. */
  readonly original: string | null
  readonly name: string
  readonly supply: readonly Slice[]
  readonly installation: readonly Slice[]
}

const LINKED = [
  { value: 'SUPPLY', label: 'Supply' },
  { value: 'INSTALLATION', label: 'Installation' },
  { value: 'NONE', label: 'None' },
]

const pctNum = (v: string): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : NaN
}

/** Hundredths, so 33.33 + 33.33 + 33.34 is exactly 100. */
const totalOf = (slices: readonly Slice[]): number =>
  slices.reduce((sum, s) => sum + Math.round(pctNum(s.percentage) * 100), 0) / 100

/** Everything the dialog refuses, in the reference's own words where it has them. */
export const termProblems = (draft: Draft, otherNames: readonly string[]): string[] => {
  const out: string[] = []
  const name = draft.name.trim()
  if (name === '') out.push('Give the term a name.')
  if (otherNames.some((n) => n.toLowerCase() === name.toLowerCase())) out.push('Another term already has that name.')
  if (/^(advance|retention|retention money|security deposit)$/i.test(name)) {
    out.push('Advance and Retention are not billing terms. Record them as invoice adjustments.')
  }
  if (draft.supply.length + draft.installation.length === 0) out.push('Add at least one line.')
  for (const [label, slices] of [['Supply value', draft.supply], ['Installation value', draft.installation]] as const) {
    if (slices.length === 0) continue
    if (slices.some((s) => s.name.trim() === '' || !(pctNum(s.percentage) > 0))) {
      out.push(`${label}: every line needs a name and a percentage.`)
    }
    const total = totalOf(slices)
    if (total !== 100) out.push(`${label} adds to ${total}%. It must be 100% or have no lines.`)
  }
  for (const [link, label] of [['SUPPLY', 'Supply'], ['INSTALLATION', 'Installation']] as const) {
    const n = [...draft.supply, ...draft.installation].filter((s) => s.linkedTo === link).length
    if (n > 1) out.push(`Only one line may be linked to ${label}.`)
  }
  return out
}

let keyCounter = 0
const nextKey = (): string => `slice-${(keyCounter += 1)}`

const termNames = (lines: readonly LineRow[]): string[] =>
  [...new Set(lines.map((l) => asText(l['termName']) || '(unnamed)'))]

const draftFor = (lines: readonly LineRow[], name: string): Draft => {
  const mine = lines.filter((l) => (asText(l['termName']) || '(unnamed)') === name)
  const slice = (l: LineRow): Slice => ({
    key: asText(l['id']) || nextKey(),
    ...(asText(l['id']) ? { id: asText(l['id']) } : {}),
    seq: Number(l['seq']) || undefined,
    name: asText(l['description']),
    percentage: asText(l['percentage']).replace(/\.?0+$/, '') || '',
    linkedTo: asText(l['linkedTo']) || 'NONE',
  } as Slice)
  return {
    original: name,
    name,
    supply: mine.filter((l) => l['termSection'] === 'SUPPLY').map(slice),
    installation: mine.filter((l) => l['termSection'] === 'INSTALLATION').map(slice),
  }
}

/** The term's slices back as BILLING_TERM lines, keeping ids and seqs where they had them. */
const linesFor = (draft: Draft, nextSeq: () => number): LineRow[] => {
  const out: LineRow[] = []
  for (const [half, slices] of [['SUPPLY', draft.supply], ['INSTALLATION', draft.installation]] as const) {
    for (const s of slices) {
      out.push({
        ...(s.id ? { id: s.id } : {}),
        seq: s.seq ?? nextSeq(),
        section: 'BILLING_TERM',
        termName: draft.name.trim(),
        description: s.name.trim(),
        termSection: half,
        percentage: s.percentage,
        linkedTo: s.linkedTo,
      })
    }
  }
  return out
}

export const BillingTerms = ({
  lines, readOnly, onChange,
}: {
  /** This document's BILLING_TERM lines only. */
  lines: readonly LineRow[]
  readOnly: boolean
  onChange: (next: readonly LineRow[]) => void
}) => {
  const [draft, setDraft] = useState<Draft | null>(null)
  const names = termNames(lines)

  let seqCursor = lines.reduce((m, l) => Math.max(m, Number(l['seq']) || 0), 0)
  const nextSeq = (): number => (seqCursor += 1)

  const rows = names.map((name, i) => {
    const mine = lines.filter((l) => (asText(l['termName']) || '(unnamed)') === name)
    const pct = (half: Half) => mine
      .filter((l) => l['termSection'] === half)
      .reduce((sum, l) => sum + Math.round(Number(l['percentage'] ?? 0) * 100), 0) / 100
    return { n: i + 1, name, supply: pct('SUPPLY'), installation: pct('INSTALLATION') }
  })

  const singleStage = (): void => onChange([
    ...lines,
    ...linesFor({
      original: null,
      name: 'Single stage',
      supply: [{ key: nextKey(), name: 'Supply', percentage: '100', linkedTo: 'SUPPLY' }],
      installation: [{ key: nextKey(), name: 'Installation', percentage: '100', linkedTo: 'INSTALLATION' }],
    }, nextSeq),
  ])

  const save = (d: Draft): void => {
    const others = lines.filter((l) => (asText(l['termName']) || '(unnamed)') !== d.original)
    onChange([...others, ...linesFor(d, nextSeq)])
    setDraft(null)
  }

  const removeTerm = (name: string): void => {
    onChange(lines.filter((l) => (asText(l['termName']) || '(unnamed)') !== name))
    setDraft(null)
  }

  const columns: ReadonlyArray<ColumnDef<(typeof rows)[number]>> = [
    { key: 'n', header: '#', width: 56 },
    {
      key: 'name', header: 'Billing term',
      render: (r) => (
        <button type="button" className="link-button" onClick={() => setDraft(draftFor(lines, r.name))}>{r.name}</button>
      ),
    },
    { key: 'supply', header: '% of supply', width: 140, align: 'right', render: (r) => <span className="num">{r.supply}</span> },
    { key: 'installation', header: '% of installation', width: 170, align: 'right', render: (r) => <span className="num">{r.installation}</span> },
  ]

  return (
    <>
      {rows.length === 0 ? (
        <p className="helper">
          {readOnly ? 'No billing terms.' : (
            <>
              Use{' '}
              <button type="button" className="link-button" onClick={singleStage}>single-stage billing (100%)</button>
              {' '}or manually add billing terms.
            </>
          )}
        </p>
      ) : (
        <Table columns={columns} rows={rows} getKey={(r) => r.name} />
      )}
      {readOnly ? null : (
        <div className="row" style={{ marginTop: 16 }}>
          <Button kind="ghost" onClick={() => setDraft({ original: null, name: '', supply: [], installation: [] })}>
            Add a billing term
          </Button>
        </div>
      )}
      {draft ? (
        <TermDialog
          draft={draft}
          readOnly={readOnly}
          otherNames={names.filter((n) => n !== draft.original)}
          onChange={setDraft}
          onClose={() => setDraft(null)}
          onDone={save}
          onRemove={draft.original === null ? undefined : () => removeTerm(draft.original!)}
        />
      ) : null}
    </>
  )
}

const TermDialog = ({
  draft, readOnly, otherNames, onChange, onClose, onDone, onRemove,
}: {
  draft: Draft
  readOnly: boolean
  otherNames: readonly string[]
  onChange: (d: Draft) => void
  onClose: () => void
  onDone: (d: Draft) => void
  onRemove?: () => void
}) => {
  const problems = termProblems(draft, otherNames)

  const section = (half: 'supply' | 'installation', title: string) => {
    const slices = draft[half]
    const set = (next: readonly Slice[]) => onChange({ ...draft, [half]: next })
    const patch = (key: string, values: Partial<Slice>) =>
      set(slices.map((s) => (s.key === key ? { ...s, ...values } : s)))
    return (
      <div style={{ marginTop: 18 }}>
        <h3 className="subhead">{title}</h3>
        {slices.length === 0 ? <p className="helper">No lines.</p> : (
          <table className="totals" style={{ width: '100%' }}>
            <thead>
              <tr><th style={{ textAlign: 'left' }}>Name</th><th>Percentage</th><th>Linked to</th><th /></tr>
            </thead>
            <tbody>
              {slices.map((s) => (
                <tr key={s.key}>
                  <td>
                    <Input aria-label={`${title} line name`} value={s.name} disabled={readOnly}
                      onChange={(name) => patch(s.key, { name })} />
                  </td>
                  <td style={{ width: 130 }}>
                    <Input aria-label={`${title} percentage`} align="right" value={s.percentage} disabled={readOnly}
                      onChange={(percentage) => patch(s.key, { percentage })} />
                  </td>
                  <td style={{ width: 170 }}>
                    <Select value={s.linkedTo} options={LINKED} disabled={readOnly}
                      onChange={(linkedTo) => patch(s.key, { linkedTo })} />
                  </td>
                  <td style={{ width: 60 }}>
                    {readOnly ? null : (
                      <Button kind="ghost" aria-label="Remove line" onClick={() => set(slices.filter((x) => x.key !== s.key))}>×</Button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="is-total">
                <td>Total</td>
                <td className="num" style={{ textAlign: 'right' }}>{totalOf(slices)}%</td>
                <td colSpan={2} />
              </tr>
            </tbody>
          </table>
        )}
        {readOnly ? null : (
          <Button kind="ghost" onClick={() => set([...slices, { key: nextKey(), name: '', percentage: '', linkedTo: 'NONE' }])}>
            Add line
          </Button>
        )}
      </div>
    )
  }

  return (
    <Modal
      title="Billing term"
      wide
      onClose={onClose}
      actions={(
        <>
          {onRemove && !readOnly ? <Button kind="ghost" onClick={onRemove}>Remove</Button> : null}
          <Button kind="ghost" onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
          {readOnly ? null : (
            <Button kind="primary" disabled={problems.length > 0} onClick={() => onDone(draft)}>Done</Button>
          )}
        </>
      )}
    >
      <p className="helper">
        A billing term cuts a BOQ line's value into the slices it is billed in.
        a) At least one line. b) Each section adds to 100%, or has no lines.
        c) Only one line linked to Supply and one to Installation. d) No Advance or Retention.
      </p>
      <Field label="Name" required>
        <Input value={draft.name} disabled={readOnly} onChange={(name) => onChange({ ...draft, name })} placeholder="Pro-Rata" />
      </Field>
      {section('supply', 'Supply value')}
      {section('installation', 'Installation value')}
      {!readOnly && problems.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <Notice kind="warn">{problems.join(' ')}</Notice>
        </div>
      ) : null}
    </Modal>
  )
}
